import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import './DiseaseDetails.css';
import diseaseContent from '../data/diseaseContent';
import aDiagram from '../images/Adiagram.png';
import arteryIcon from '../images/artery.png';
import lysosomeIcon from '../images/lysosome.png';
import pkuIcon from '../images/PKUmolecule.png';
import diabetesIcon from '../images/Diabetes.png';
import galactosemiaIcon from '../images/Galactosemia.png';
import lcellsImage from '../images/Lcells.png';
import ldegredationImage from '../images/Ldegredation.png';
import lpathwayImage from '../images/Lpathway.png';
import pphenotypeImage from '../images/Pphenotype.png';
import pcountriesImage from '../images/Pcountries.png';
import pgeneticsImage from '../images/Pgenetics.png';
import pdiagnosisImage from '../images/Pdiagnosis.png';
import dInsulinPathImage from '../images/DInsulinPath.png';
import dT1Image from '../images/DT1.png';
import dT2Image from '../images/DT2.png';
import dT1RiskFactorsImage from '../images/DT1RiskFactors.png';
import dT2RiskFactorsImage from '../images/DT2RiskFactors.png';
import gBabyImage from '../images/Gbaby.png';
import gPathwayImage from '../images/Gpathway.png';
import pPathwayImage from '../images/Ppathway.png';
import dStagesImage from '../images/DStages.png';
import aRiskFactorsImage from '../images/ARiskFactors.png';
import aStentImage from '../images/AStent.png';

function DiseaseDetails() {
  const { disease, section } = useParams();
  
  // State for tracking checked sections
  const [checkedDiseases, setCheckedDiseases] = useState(() => {
    const saved = localStorage.getItem('checkedDiseases');
    return saved ? JSON.parse(saved) : {};
  });
  
  // State for diagnosis dropdown
  const [openDiagnoses, setOpenDiagnoses] = useState({
    diagnosis1: false,
    diagnosis2: false
  });
  
  // Format disease name to match format in Home component
  const getFormattedDiseaseName = () => {
    if (disease.toLowerCase() === 'pku') {
      return 'Phenylketonuria (PKU)';
    } else if (disease === 'tay-sachs') {
      return 'Tay-Sachs Disease';
    } else if (disease === 'gaucher') {
      return 'Gaucher Disease';
    } else if (disease === 'niemann-pick') {
      return 'Niemann-Pick Disease';
    } else if (disease === 'diabetes') {
      return 'Diabetes Mellitus';
    } else {
      return disease.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    }
  };
  
  // Format section name to match format in Home component
  const getFormattedSectionName = () => {
    if (section === 'diagnosis-and-treatment') {
      return 'Diagnosis & Treatment';
    } else if (section === 'risk-factors') {
      return 'Risk Factors';
    } else if (section === 'learning-tools') {
      return 'Learning Tools';
    } else {
      return section.charAt(0).toUpperCase() + section.slice(1);
    }
  };
  
  // Handle checkbox change
  const handleCheck = () => {
    const diseaseName = getFormattedDiseaseName();
    const sectionName = getFormattedSectionName();
    
    const newCheckedDiseases = {
      ...checkedDiseases,
      [diseaseName]: {
        ...checkedDiseases[diseaseName],
        [sectionName]: !checkedDiseases[diseaseName]?.[sectionName]
      }
    };
    
    setCheckedDiseases(newCheckedDiseases);
    localStorage.setItem('checkedDiseases', JSON.stringify(newCheckedDiseases));
  };
  
  // Modify the useEffect for scrolling
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [section]);

  const sections = [
    { id: 'pathogenesis', icon: '🔬' },
    { id: 'symptoms', icon: '🏥', label: 'Signs & Symptoms' },
    { id: 'prognosis', icon: '📈' },
    { id: 'risk-factors', icon: '⚠️' },
    { id: 'diagnosis-and-treatment', icon: '💊', label: 'Diagnosis & Treatment' },
    { id: 'learning-tools', icon: '📚' }
  ];

  // Find current section index
  const currentIndex = sections.findIndex(s => s.id === section);
  const prevSection = sections[currentIndex - 1];
  const nextSection = sections[currentIndex + 1];

  const formatText = (text) => {
    if (typeof text !== 'string') {
      return text;
    }
    
    const linkRegex = /<Link to="([^"]+)"[^>]*>([^<]+)<\/Link>/g;
    
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = linkRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        const beforeText = text.substring(lastIndex, match.index);
        parts.push(processColons(beforeText));
      }
      
      const url = match[1];
      const linkText = match[2];
      
      if (url.startsWith('http')) {
        parts.push(
          <a 
            key={match.index} 
            href={url}
            className="content-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            {linkText}
          </a>
        );
      } else {
        parts.push(
          <Link 
            key={match.index} 
            to={url} 
            className="content-link"
          >
            {linkText}
          </Link>
        );
      }
      
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) {
      const remainingText = text.substring(lastIndex);
      parts.push(processColons(remainingText));
    }
    
    return parts;
  };

  const processColons = (text) => {
    if (text.match(/https?:\/\//)) {
      return text;
    }
    
    if (text.includes(':')) {
      const colonIndex = text.indexOf(':');
      const beforeColon = text.substring(0, colonIndex).trim();
      const afterColon = text.substring(colonIndex + 1);
      
      return (
        <>
          <strong>{beforeColon}</strong>:{afterColon}
        </>
      );
    }
    
    return text;
  };

  const formatListItems = (items) => {
    return (
      <div className="list-container">
        {items.map((item, index) => {
          const indentLevel = item.match(/^\s*/)[0].length;
          return (
            <div 
              key={index} 
              className="list-item"
              style={{ marginLeft: `${indentLevel * 1.5}rem` }}
            >
              {formatText(item.replace(/^[-•\s]+/, '').trim())}
            </div>
          );
        })}
      </div>
    );
  };

  // Special rendering for learning tools section
  const renderLearningTools = (content) => {
    if (!content) return null;
    
    // Split content into sections but exclude Case-Study-Diagnosis
    const processedContent = content.split('\n\n')
      .filter(paragraph => !paragraph.trim().startsWith('Case-Study-Diagnosis'))
      .map((paragraph, paragraphIndex) => {
      if (paragraph.includes("AI-GENERATED FLASHCARDS") || paragraph.includes("AI-generated Flashcards")) {
        return (
          <div key={`flashcards-${paragraphIndex}`} className="content-section">
            <div className="section-header">
              <strong>{paragraph.split('\n')[0].trim()}</strong>
            </div>
            <div className="content-block">
              {paragraph.split('\n').slice(1).map((line, lineIndex) => {
                if (line.trim().startsWith('-')) {
                  return formatListItems([line]);
                } else if (line.trim()) {
                  return (
                    <p key={`flashcard-${lineIndex}`} className="content-text">
                      {formatText(line.trim())}
                    </p>
                  );
                }
                return null;
              })}
            </div>
          </div>
        );
      }
      return formatRegularParagraph(paragraph, paragraphIndex);
    });
    
    // Create diagnosis sections based on the disease
    let diagnosisSections = [];
    
    if (disease === 'atherosclerosis') {
      diagnosisSections = [
        <div key="diagnosis" className="diagnosis-section">
          <button 
            className="diagnosis-toggle"
            onClick={() => setOpenDiagnoses(prev => ({
              ...prev,
              diagnosis1: !prev.diagnosis1
            }))}
            aria-expanded={openDiagnoses.diagnosis1}
          >
            <span>Diagnosis</span>
            <span className="toggle-icon">
              {openDiagnoses.diagnosis1 ? '−' : '+'}
            </span>
          </button>
          {openDiagnoses.diagnosis1 && (
            <div className="diagnosis-content">
              <p>Blood tests showed abnormal cholesterol levels: high LDL and low HDL. Imaging tests confirmed significant blockages in two major heart arteries and plaques in his neck arteries, confirming atherosclerosis. The ultimate diagnosis was a heart attack caused by atherosclerosis.</p>
            </div>
          )}
        </div>
      ];
    } else if (disease === 'diabetes') {
      diagnosisSections = [
        <div key="diagnosis-1" className="diagnosis-section">
          <button 
            className="diagnosis-toggle"
            onClick={() => setOpenDiagnoses(prev => ({
              ...prev,
              diagnosis1: !prev.diagnosis1
            }))}
            aria-expanded={openDiagnoses.diagnosis1}
          >
            <span>Diagnosis 1</span>
            <span className="toggle-icon">
              {openDiagnoses.diagnosis1 ? '−' : '+'}
            </span>
          </button>
          {openDiagnoses.diagnosis1 && (
            <div className="diagnosis-content">
              <p>The results confirmed Type 1 Diabetes Mellitus, an autoimmune condition in which the body attacks and destroys insulin-producing cells in the pancreas, leading to a complete lack of insulin production. Her presentation of polyuria, polydipsia, weight loss, and hyperglycemia was consistent with this diagnosis.</p>
            </div>
          )}
        </div>,
        <div key="diagnosis-2" className="diagnosis-section">
          <button 
            className="diagnosis-toggle"
            onClick={() => setOpenDiagnoses(prev => ({
              ...prev,
              diagnosis2: !prev.diagnosis2
            }))}
            aria-expanded={openDiagnoses.diagnosis2}
          >
            <span>Diagnosis 2</span>
            <span className="toggle-icon">
              {openDiagnoses.diagnosis2 ? '−' : '+'}
            </span>
          </button>
          {openDiagnoses.diagnosis2 && (
            <div className="diagnosis-content">
              <p>The patient was diagnosed with Type 2 Diabetes Mellitus, a metabolic disorder characterized by insulin resistance and progressive pancreatic β-cell dysfunction. His elevated HbA1c, fasting hyperglycemia, and clinical presentation were consistent with the disease.</p>
            </div>
          )}
        </div>
      ];
    } else if (disease === 'tay-sachs') {
      diagnosisSections = [
        <div key="diagnosis" className="diagnosis-section">
          <button 
            className="diagnosis-toggle"
            onClick={() => setOpenDiagnoses(prev => ({
              ...prev,
              diagnosis1: !prev.diagnosis1
            }))}
            aria-expanded={openDiagnoses.diagnosis1}
          >
            <span>Diagnosis</span>
            <span className="toggle-icon">
              {openDiagnoses.diagnosis1 ? '−' : '+'}
            </span>
          </button>
          {openDiagnoses.diagnosis1 && (
            <div className="diagnosis-content">
              <p>Enzyme assay confirms a deficiency in hexosaminidase A activity, supporting a diagnosis of Tay-Sachs disease. Genetic testing identifies mutations in the HEXA gene.</p>
            </div>
          )}
        </div>
      ];
    } else if (disease === 'gaucher') {
      diagnosisSections = [
        <div key="diagnosis" className="diagnosis-section">
          <button 
            className="diagnosis-toggle"
            onClick={() => setOpenDiagnoses(prev => ({
              ...prev,
              diagnosis1: !prev.diagnosis1
            }))}
            aria-expanded={openDiagnoses.diagnosis1}
          >
            <span>Diagnosis</span>
            <span className="toggle-icon">
              {openDiagnoses.diagnosis1 ? '−' : '+'}
            </span>
          </button>
          {openDiagnoses.diagnosis1 && (
            <div className="diagnosis-content">
              <p>Enzyme assay reveals reduced glucocerebrosidase activity, confirming Gaucher disease. Genetic testing identifies mutations in the GBA gene.</p>
            </div>
          )}
        </div>
      ];
    } else if (disease === 'niemann-pick') {
      diagnosisSections = [
        <div key="diagnosis" className="diagnosis-section">
          <button 
            className="diagnosis-toggle"
            onClick={() => setOpenDiagnoses(prev => ({
              ...prev,
              diagnosis1: !prev.diagnosis1
            }))}
            aria-expanded={openDiagnoses.diagnosis1}
          >
            <span>Diagnosis</span>
            <span className="toggle-icon">
              {openDiagnoses.diagnosis1 ? '−' : '+'}
            </span>
          </button>
          {openDiagnoses.diagnosis1 && (
            <div className="diagnosis-content">
              <p>Laboratory testing confirms deficient sphingomyelinase activity, and genetic testing reveals mutations in the SMPD1 gene, consistent with Niemann-Pick disease Type A/B. The presence of a cherry-red macula and early onset of symptoms suggests Type A.</p>
            </div>
          )}
        </div>
      ];
    } else if (disease === 'galactosemia') {
      diagnosisSections = [
        <div key="diagnosis" className="diagnosis-section">
          <button 
            className="diagnosis-toggle"
            onClick={() => setOpenDiagnoses(prev => ({
              ...prev,
              diagnosis1: !prev.diagnosis1
            }))}
            aria-expanded={openDiagnoses.diagnosis1}
          >
            <span>Diagnosis</span>
            <span className="toggle-icon">
              {openDiagnoses.diagnosis1 ? '−' : '+'}
            </span>
          </button>
          {openDiagnoses.diagnosis1 && (
            <div className="diagnosis-content">
              <p>Blood tests revealed hypoglycemia, elevated bilirubin, and liver dysfunction. A urinalysis showed reducing substances, suggesting the presence of unmetabolized sugars. Newborn screening returned positive for galactosemia, and confirmatory testing revealed low galactose-1-phosphate uridyltransferase (GALT) enzyme activity, confirming the diagnosis of classic galactosemia.</p>
            </div>
          )}
        </div>
      ];
    } else if (disease === 'pku') {
      diagnosisSections = [
        <div key="diagnosis" className="diagnosis-section">
        <button 
          className="diagnosis-toggle"
            onClick={() => setOpenDiagnoses(prev => ({
              ...prev,
              diagnosis1: !prev.diagnosis1
            }))}
            aria-expanded={openDiagnoses.diagnosis1}
          >
            <span>Diagnosis</span>
          <span className="toggle-icon">
              {openDiagnoses.diagnosis1 ? '−' : '+'}
          </span>
        </button>
          {openDiagnoses.diagnosis1 && (
          <div className="diagnosis-content">
              <p>Based on the newborn screening and confirmatory blood tests, elevated phenylalanine is the primary biomarker for PKU, suggesting an inability to metabolize this amino acid properly. Additionally, a low tyrosine level suggests a deficiency in PAH. A low-phenylalanine diet and long-term monitoring were suggested for treatment.</p>
          </div>
        )}
      </div>
      ];
    }
    // Add similar blocks for other diseases...
    
    // Find the index of the flashcards section
    const flashcardsIndex = processedContent.findIndex(
      component => component && component.props && 
      (component.props.className === "content-section" || 
       component.props.children?.some?.(child => 
         child.props?.className === "section-header" && 
         child.props.children.props.children.includes("Flashcards")
       ))
    );
    
    // Insert the diagnosis sections before the flashcards or at the end if no flashcards
    if (flashcardsIndex !== -1 && diagnosisSections.length > 0) {
      processedContent.splice(flashcardsIndex, 0, ...diagnosisSections);
    } else if (diagnosisSections.length > 0) {
      // If no flashcards section is found, append the diagnosis sections
      processedContent.push(...diagnosisSections);
    }
    
    return processedContent;
  };

  const formatRegularParagraph = (paragraph, paragraphIndex) => {
    const lines = paragraph.split('\n');
    const contentElements = [];
    
    if (lines[0] && /^[A-Z\s]+:$/.test(lines[0].trim())) {
      // This is a main section header with a colon
      const headerText = lines[0].trim().replace(':', '');
      contentElements.push(
        <div key={`section-header-${paragraphIndex}`} className="section-header">
          <strong>{headerText}</strong>
        </div>
      );
      
      // Process the rest of the lines
      const remainingLines = lines.slice(1);
      if (remainingLines.length > 0) {
        let currentContent = [];
        
        remainingLines.forEach((line, lineIndex) => {
          if (line.trim()) {
            if (line.includes(':') && !line.startsWith('-')) {
              // This is a subheading
              // If we have accumulated content, add it before the subheading
              if (currentContent.length > 0) {
                contentElements.push(
                  <div key={`content-${lineIndex}`} className="line-content">
                    {currentContent}
                  </div>
                );
                currentContent = [];
              }
              
              const [title, ...rest] = line.split(':');
              contentElements.push(
                <div key={`subheading-${lineIndex}`} className="content-subheading">
                  <strong>{title.trim()}</strong>
                </div>
              );
              
              // Process any content after the colon
              if (rest.length > 0 && rest[0].trim()) {
                currentContent.push(
                  <p key={`after-colon-${lineIndex}`} className="content-text">
                    {formatText(rest.join(':').trim())}
                  </p>
                );
              }
            } else if (line.trim().startsWith('-')) {
              // Handle bullet points
              currentContent.push(formatListItems([line]));
            } else if (line.trim()) {
              // Handle regular text
              currentContent.push(
                <p key={`text-${lineIndex}`} className="content-text">
                  {formatText(line.trim())}
                </p>
              );
            }
          }
        });
        
        // Add any remaining content
        if (currentContent.length > 0) {
          contentElements.push(
            <div key="remaining-content" className="line-content">
              {currentContent}
            </div>
          );
        }
      }
    } 
    // Check if this is an all-caps header without a colon (like "TYPES AND MECHANISMS")
    else if (lines[0] && lines[0].toUpperCase() === lines[0] && !lines[0].includes(':')) {
      // This is a main section header without a colon
      contentElements.push(
        <div key={`section-header-${paragraphIndex}`} className="section-header">
          <strong>{lines[0]}</strong>
        </div>
      );
      
      // Process the rest of the lines
      const remainingLines = lines.slice(1);
      if (remainingLines.length > 0) {
        let currentContent = [];
        
        remainingLines.forEach((line, lineIndex) => {
          if (line.trim()) {
            if (line.includes(':') && !line.startsWith('-')) {
              // This is a subheading
              // If we have accumulated content, add it before the subheading
              if (currentContent.length > 0) {
                contentElements.push(
                  <div key={`content-${lineIndex}`} className="line-content">
                    {currentContent}
                  </div>
                );
                currentContent = [];
              }
              
              const [title, ...rest] = line.split(':');
              contentElements.push(
                <div key={`subheading-${lineIndex}`} className="content-subheading">
                  <strong>{title.trim()}</strong>
                </div>
              );
              
              // Process any content after the colon
              if (rest.length > 0 && rest[0].trim()) {
                currentContent.push(
                  <p key={`after-colon-${lineIndex}`} className="content-text">
                    {formatText(rest.join(':').trim())}
                  </p>
                );
              }
            } else if (line.trim().startsWith('-')) {
              // Handle bullet points
              currentContent.push(formatListItems([line]));
            } else if (line.trim()) {
              // Handle regular text
              currentContent.push(
                <p key={`text-${lineIndex}`} className="content-text">
                  {formatText(line.trim())}
                </p>
              );
            }
          }
        });
        
        // Add any remaining content
        if (currentContent.length > 0) {
          contentElements.push(
            <div key="remaining-content" className="line-content">
              {currentContent}
            </div>
          );
        }
      }
    }
    // Check if this paragraph contains regular subheadings
    else if (lines.some(line => line.includes(':') && !line.startsWith('-'))) {
      // Process lines to separate content and subheadings
      let currentContent = [];
      
      lines.forEach((line, lineIndex) => {
        if (line.includes(':') && !line.startsWith('-')) {
          // If we have accumulated content, add it before the subheading
          if (currentContent.length > 0) {
            contentElements.push(
              <div key={`content-${lineIndex}`} className="line-content">
                {currentContent}
              </div>
            );
            currentContent = [];
          }
          
          // Add the subheading
          const [title, ...rest] = line.split(':');
          contentElements.push(
            <div key={`subheading-${lineIndex}`} className="content-subheading">
              <strong>{title.trim()}</strong>
            </div>
          );
          
          // Process any content after the colon
          if (rest.length > 0 && rest[0].trim()) {
            currentContent.push(
              <p key={`after-colon-${lineIndex}`} className="content-text">
                {formatText(rest.join(':').trim())}
              </p>
            );
          }
        } else if (line.trim().startsWith('-')) {
          // Handle bullet points
          currentContent.push(formatListItems([line]));
        } else if (line.trim()) {
          // Handle regular text
          currentContent.push(
            <p key={`text-${lineIndex}`} className="content-text">
              {formatText(line.trim())}
            </p>
          );
        }
      });
      
      // Add any remaining content
      if (currentContent.length > 0) {
        contentElements.push(
          <div key="remaining-content" className="line-content">
            {currentContent}
          </div>
        );
      }
    } else {
      // Regular paragraph without subheadings - process normally
      contentElements.push(
        lines.map((line, lineIndex) => {
          const trimmedLine = line.trim();
          if (trimmedLine.startsWith('-')) {
            return formatListItems([trimmedLine]);
          }
          return (
            <p key={lineIndex} className="content-text">
              {formatText(trimmedLine)}
            </p>
          );
        })
      );
    }
    
    return (
      <div key={paragraphIndex} className="content-block">
        {contentElements}
      </div>
    );
  };

  const formatContent = (content) => {
    if (!content) return null;
    
    // Special handling for learning-tools section
    if (section === 'learning-tools') {
      return renderLearningTools(content);
    }

    // PKU pathogenesis section
    if (disease === 'pku' && section === 'pathogenesis') {
      return (
        <>
          {content.split('\n\n').map((paragraph, paragraphIndex) => 
            formatRegularParagraph(paragraph, paragraphIndex)
          )}
          <div className="diagram-row">
            <div className="diagram-container">
              <img 
                src={pphenotypeImage} 
                alt="PKU Phenotype Diagram" 
                className="content-diagram"
              />
            </div>
            <div className="diagram-container">
              <img 
                src={pPathwayImage} 
                alt="PKU Pathway Diagram" 
                className="content-diagram"
              />
            </div>
          </div>
        </>
      );
    }

    // PKU risk factors section
    if (disease === 'pku' && section === 'risk-factors') {
      return (
        <>
          {content.split('\n\n').map((paragraph, paragraphIndex) => 
            formatRegularParagraph(paragraph, paragraphIndex)
          )}
          <div className="diagram-row">
            <div className="diagram-container">
              <img 
                src={pcountriesImage} 
                alt="PKU Distribution by Country" 
                className="content-diagram"
              />
            </div>
            <div className="diagram-container">
              <img 
                src={pgeneticsImage} 
                alt="PKU Genetics" 
                className="content-diagram"
              />
            </div>
          </div>
        </>
      );
    }

    // PKU diagnosis and treatment section
    if (disease === 'pku' && section === 'diagnosis-and-treatment') {
      const paragraphs = content.split('\n\n');
      const diagnosisEndIndex = paragraphs.findIndex(p => 
        p.toLowerCase().includes('treatment:') || 
        p.toLowerCase().includes('treatments:')
      );

      if (diagnosisEndIndex !== -1) {
        return (
          <>
            {paragraphs.slice(0, diagnosisEndIndex).map((paragraph, paragraphIndex) => 
              formatRegularParagraph(paragraph, paragraphIndex)
            )}
            
            <div className="diagram-container pku-diagnosis">
              <img 
                src={pdiagnosisImage} 
                alt="PKU Diagnosis Process" 
                className="content-diagram"
              />
            </div>
            
            {paragraphs.slice(diagnosisEndIndex).map((paragraph, paragraphIndex) => 
              formatRegularParagraph(paragraph, paragraphIndex + diagnosisEndIndex)
            )}
          </>
        );
      }
    }

    // Atherosclerosis prognosis section
    if (disease === 'atherosclerosis' && section === 'prognosis') {
      return (
        <>
          {content.split('\n\n').map((paragraph, paragraphIndex) => 
            formatRegularParagraph(paragraph, paragraphIndex)
          )}
          <div className="diagram-container">
            <img 
              src={aDiagram} 
              alt="Atherosclerosis Diagram" 
              className="content-diagram"
            />
          </div>
          <p className="figure-caption">
            Fig. 1. Macrophage profiling in atherosclerosis: understanding the unstable plaque. Stable plaques have thick fibrous caps with negligible underlying atheromatous cores and minimal inflammation. Vulnerable plaques have thin fibrous caps, more inflammation, and large lipid cores[7].
          </p>
        </>
      );
    }

    // Lysosomal storage diseases pathogenesis sections
    if (section === 'pathogenesis') {
      if (disease === 'tay-sachs') {
        return (
          <>
            {content.split('\n\n').map((paragraph, paragraphIndex) => 
              formatRegularParagraph(paragraph, paragraphIndex)
            )}
            <div className="diagram-container half-width">
              <img 
                src={lpathwayImage} 
                alt="Lysosomal Disease Pathway" 
                className="content-diagram"
              />
              <span className="image-citation">[2]</span>
            </div>
          </>
        );
      } else if (disease === 'gaucher') {
        return (
          <>
            {content.split('\n\n').map((paragraph, paragraphIndex) => 
              formatRegularParagraph(paragraph, paragraphIndex)
            )}
            <div className="diagram-container">
              <img 
                src={ldegredationImage} 
                alt="Lysosomal Degradation Process" 
                className="content-diagram"
              />
              <span className="image-citation">[2]</span>
            </div>
          </>
        );
      } else if (disease === 'niemann-pick') {
        return (
          <>
            {content.split('\n\n').map((paragraph, paragraphIndex) => 
              formatRegularParagraph(paragraph, paragraphIndex)
            )}
            <div className="diagram-container">
              <img 
                src={lcellsImage} 
                alt="Lysosomal Storage Disease Cells" 
                className="content-diagram"
              />
              <span className="image-citation">[2]</span>
            </div>
          </>
        );
      }
    }

    // Diabetes symptoms section
    if (disease === 'diabetes' && section === 'symptoms') {
      const paragraphs = content.split('\n\n');
      return (
        <>
          {paragraphs.map((paragraph, paragraphIndex) => 
            formatRegularParagraph(paragraph, paragraphIndex)
          )}
        </>
      );
    }
    
    // Special handling for diabetes pathogenesis section
    if (disease === 'diabetes' && section === 'pathogenesis') {
      const paragraphs = content.split('\n\n');
      const progressionIndex = paragraphs.findIndex(p =>
        p.toLowerCase().includes('disease progression')
      );

      return (
        <>
          {/* Render content up to and including Disease progression */}
          {paragraphs.slice(0, progressionIndex + 1).map((paragraph, paragraphIndex) => 
            formatRegularParagraph(paragraph, paragraphIndex)
          )}
          
          {/* Add diabetes type images after Disease progression */}
          <div className="diagram-row">
            <div className="diagram-container dt1">
              <img 
                src={dT1Image} 
                alt="Type 1 Diabetes" 
                className="content-diagram"
              />
              <span className="image-citation">[18]</span>
            </div>
            <div className="diagram-container dt2">
              <img 
                src={dT2Image} 
                alt="Type 2 Diabetes" 
                className="content-diagram"
              />
              <span className="image-citation">[18]</span>
            </div>
          </div>
          
          {/* Render remaining content from Disease progression to end */}
          {paragraphs.slice(progressionIndex + 1).map((paragraph, paragraphIndex) => 
            formatRegularParagraph(paragraph, paragraphIndex + progressionIndex + 1)
          )}
          
          {/* Add the insulin pathway diagram */}
          <div className="diagram-container diabetes-pathway">
            <img 
              src={dInsulinPathImage} 
              alt="Diabetes Insulin Pathway" 
              className="content-diagram"
            />
          </div>

          {/* Add the stages diagram */}
          <div className="diagram-container">
            <img 
              src={dStagesImage} 
              alt="Diabetes Stages" 
              className="content-diagram"
            />
          </div>
          <p className="figure-caption">
            Fig. 2. Progressive Loss of β-Cell Function in the Development of Type 1 Diabetes Mellitus.<br/><br/>
            This figure illustrates the pathogenesis of T1D as a three-stage model. Stage 1 is marked by the presence of β-cell autoimmunity with normoglycemia, identifying individuals at risk but without symptoms. Stage 2 shows continued autoimmunity with impaired glucose tolerance, reflecting a presymptomatic phase where β-cell mass and function begin to decline. Stage 3 is defined by further β-cell loss leading to symptomatic hyperglycemia and clinical diagnosis of T1D. The downward curve represents the progressive decline in functional β-cell mass over time as the disease advances.
          </p>
        </>
      );
    }
    
    // Update the diabetes risk factors condition in formatContent
    if (disease === 'diabetes' && section === 'risk-factors') {
      const paragraphs = content.split('\n\n');
      const t2Index = paragraphs.findIndex(p => 
        p.toLowerCase().includes('type 2 diabetes risk factors') ||
        p.includes('T2D risk factors')
      );

      return (
        <>
          {/* Render Type 1 content */}
          {paragraphs.slice(0, t2Index).map((paragraph, paragraphIndex) => 
            formatRegularParagraph(paragraph, paragraphIndex)
          )}
          
          {/* Add T1D risk factors image */}
          <div className="diagram-container">
            <img 
              src={dT1RiskFactorsImage} 
              alt="Type 1 Diabetes Risk Factors" 
              className="content-diagram"
            />
          </div>
          
          {/* Render Type 2 content */}
          {paragraphs.slice(t2Index).map((paragraph, paragraphIndex) => 
            formatRegularParagraph(paragraph, paragraphIndex + t2Index)
          )}
          
          {/* Add T2D risk factors image */}
          <div className="diagram-container">
            <img 
              src={dT2RiskFactorsImage} 
              alt="Type 2 Diabetes Risk Factors" 
              className="content-diagram"
            />
          </div>
        </>
      );
    }
    
    // Add condition for galactosemia symptoms in the formatContent function
    if (disease === 'galactosemia' && section === 'symptoms') {
      return (
        <>
          {content.split('\n\n').map((paragraph, paragraphIndex) => 
            formatRegularParagraph(paragraph, paragraphIndex)
          )}
          <div className="diagram-container">
            <img 
              src={gBabyImage} 
              alt="Galactosemia Baby Symptoms" 
              className="content-diagram"
            />
          </div>
        </>
      );
    }
    
    // Update the galactosemia pathogenesis condition
    if (disease === 'galactosemia' && section === 'pathogenesis') {
      return (
        <>
          {content.split('\n\n').map((paragraph, paragraphIndex) => 
            formatRegularParagraph(paragraph, paragraphIndex)
          )}
          <div className="diagram-container">
            <img 
              src={gPathwayImage} 
              alt="Galactosemia Pathway" 
              className="content-diagram"
            />
            <p className="figure-caption">
              Fig. 3. Involved pathways and pathophysiological agents in hereditary galactosemia, with red arrows indicating inhibition and green arrows indicating induction.
            </p>
          </div>
        </>
      );
    }
    
    // Add this condition in the formatContent function, after the atherosclerosis risk-factors condition
    if (disease === 'atherosclerosis' && section === 'risk-factors') {
      const paragraphs = content.split('\n\n');
      const modifiableIndex = paragraphs.findIndex(p => 
        p.toLowerCase().includes('modifiable risk factors:')
      );

      return (
        <>
          {/* Render content up to modifiable risk factors */}
          {paragraphs.slice(0, modifiableIndex + 1).map((paragraph, paragraphIndex) => 
            formatRegularParagraph(paragraph, paragraphIndex)
          )}
          
          {/* Add the risk factors diagram with reduced margins */}
          <div className="diagram-container half-width risk-factors">
            <img 
              src={aRiskFactorsImage} 
              alt="Atherosclerosis Risk Factors" 
              className="content-diagram"
            />
          </div>
          
          {/* Render remaining content */}
          {paragraphs.slice(modifiableIndex + 1).map((paragraph, paragraphIndex) => 
            formatRegularParagraph(paragraph, paragraphIndex + modifiableIndex + 1)
          )}
        </>
      );
    }
    
    // Update the atherosclerosis diagnosis and treatment condition in formatContent
    if (disease === 'atherosclerosis' && section === 'diagnosis-and-treatment') {
      return (
        <>
          {/* Render all content first */}
          {content.split('\n\n').map((paragraph, paragraphIndex) => 
            formatRegularParagraph(paragraph, paragraphIndex)
          )}
          
          {/* Add the stent diagram without caption */}
          <div className="diagram-container half-width no-margin">
            <img 
              src={aStentImage} 
              alt="Atherosclerosis Stent Procedure" 
              className="content-diagram"
            />
          </div>
        </>
      );
    }
    
    // Regular content formatting
    return content.split('\n\n').map((paragraph, paragraphIndex) => 
      formatRegularParagraph(paragraph, paragraphIndex)
    );
  };

  // Check if this section is marked as completed
  const isChecked = () => {
    const diseaseName = getFormattedDiseaseName();
    const sectionName = getFormattedSectionName();
    return checkedDiseases[diseaseName]?.[sectionName] || false;
  };

  return (
    <div className="disease-details">
      <div className="disease-header">
        <h1 className="disease-title">
          {disease === 'atherosclerosis' && (
            <img src={arteryIcon} alt="Artery Icon" className="title-icon" />
          )}
          {(disease === 'tay-sachs' || disease === 'gaucher' || disease === 'niemann-pick') && (
            <img src={lysosomeIcon} alt="Lysosome Icon" className="title-icon" />
          )}
          {disease === 'pku' && (
            <img src={pkuIcon} alt="PKU Icon" className="title-icon" />
          )}
          {disease === 'diabetes' && (
            <img src={diabetesIcon} alt="Diabetes Icon" className="title-icon" />
          )}
          {disease === 'galactosemia' && (
            <img src={galactosemiaIcon} alt="Galactosemia Icon" className="title-icon" />
          )}
          {disease.toLowerCase() === 'pku' 
            ? 'Phenylketonuria (PKU)'
            : disease.split('-').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' ')
          }
        </h1>
        
        {diseaseContent[disease]?.about && (
          <div className="about-section">
            <h2>About</h2>
            <p className="about-text">
              {diseaseContent[disease].about.split('\n').map((line, index) => (
                <span key={index}>
                  {line}
                  {index < diseaseContent[disease].about.split('\n').length - 1 && <br />}
                </span>
              ))}
            </p>
          </div>
        )}

        <div className="section-nav">
          {sections.map(s => (
            <Link 
              key={s.id} 
              to={`/${disease}/${s.id}`}
              className={`section-link ${section === s.id ? 'active' : ''}`}
            >
              <span className="icon">{s.icon}</span>
              {s.label || s.id.split('-').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' ')}
            </Link>
          ))}
        </div>
      </div>
      <div className="content">
        {diseaseContent[disease] && diseaseContent[disease][section] ? (
          <div className="disease-content">
            <h2>
              {disease === 'atherosclerosis' && (
                <img src={arteryIcon} alt="Artery Icon" className="section-icon" />
              )}
              {(disease === 'tay-sachs' || disease === 'gaucher' || disease === 'niemann-pick') && (
                <img src={lysosomeIcon} alt="Lysosome Icon" className="section-icon" />
              )}
              {disease === 'pku' && (
                <img src={pkuIcon} alt="PKU Icon" className="section-icon" />
              )}
              {disease === 'diabetes' && (
                <img src={diabetesIcon} alt="Diabetes Icon" className="section-icon" />
              )}
              {disease === 'galactosemia' && (
                <img src={galactosemiaIcon} alt="Galactosemia Icon" className="section-icon" />
              )}
              {section.split('-').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' ')}
            </h2>
            
            {section !== 'symptoms' && section === 'learning-tools' ? (
              <div className="learning-outcome">
                <span className="warning-icon">📚</span>
                <div className="outcome-text">
                  <strong>Case Study Instructions:</strong>
                  <p>Read the following case study carefully, analyze the patient's symptoms and test results, and try to formulate a diagnosis before viewing the answer. This exercise will help develop your clinical reasoning skills.</p>
                </div>
              </div>
            ) : section !== 'symptoms' ? (
              <div className="learning-outcome">
                <span className="warning-icon">⚠️</span>
                <div className="outcome-text">
                  <strong>Associated learning outcome:</strong>
                  <p>{diseaseContent[disease][`${section}-outcome`]}</p>
                </div>
              </div>
            ) : null}
            {formatContent(diseaseContent[disease][section])}

            <div className="completion-checkbox">
              <label>
                <input 
                  type="checkbox" 
                  checked={isChecked()} 
                  onChange={handleCheck} 
                />
                Mark as completed
              </label>
            </div>
          </div>
        ) : (
          <div className="case-study">
            {/* Placeholder for case study content */}
          </div>
        )}
        <div className="navigation-buttons">
          {prevSection && (
            <Link 
              to={`/${disease}/${prevSection.id}`}
              className="nav-button back-button"
            >
              ← {prevSection.id.split('-').map(word => 
                  word.charAt(0).toUpperCase() + word.slice(1)
                ).join(' ')}
            </Link>
          )}
          {nextSection && (
            <Link 
              to={`/${disease}/${nextSection.id}`}
              className="nav-button next-button"
            >
              {nextSection.id.split('-').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' ')} →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default DiseaseDetails;